// Public customer-view booking page (/book/:companySlug) — no auth, no layout.
// Flow: pick a service → pick a date (only dates with free slots) → pick a
// slot → enter details → confirmed. "My bookings" finds bookings by phone and
// supports cancel + reschedule.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CalendarClock, CalendarDays, Check, ChevronLeft, Clock, Hash, Loader2, Search,
  StickyNote, Users, X, XCircle,
} from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { PhoneField } from '@/components/PhoneField';
import {
  publicBookingService, APPT_STATUS_META, to12h,
  type ApptBooking, type ApptService, type ApptSlot,
} from '@/services/appointments.service';

type Step = 'service' | 'date' | 'slot' | 'details' | 'done';
type Tab = 'book' | 'mine';

const niceDate = (d: string) =>
  new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

export default function PublicBooking() {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [company, setCompany] = useState<{ name: string; logoUrl?: string | null } | null>(null);
  const [services, setServices] = useState<ApptService[]>([]);
  const [tab, setTab] = useState<Tab>('book');

  // booking flow state
  const [step, setStep] = useState<Step>('service');
  const [service, setService] = useState<ApptService | null>(null);
  const [dates, setDates] = useState<Array<{ date: string; weekday: string; slotCount: number }>>([]);
  const [datesLoading, setDatesLoading] = useState(false);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState<ApptSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slot, setSlot] = useState<ApptSlot | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');
  const [booking, setBooking] = useState(false);
  const [confirmed, setConfirmed] = useState<{ bookingNo: string } | null>(null);

  // my-bookings state
  const [minePhone, setMinePhone] = useState('');
  const [mine, setMine] = useState<ApptBooking[] | null>(null);
  const [mineLoading, setMineLoading] = useState(false);
  const [cancelFor, setCancelFor] = useState<ApptBooking | null>(null);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [reschedFor, setReschedFor] = useState<ApptBooking | null>(null);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    publicBookingService.resolve(slug)
      .then((r) => {
        setCompany(r.company);
        setServices((r.services ?? []).filter((s) => s.isActive));
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const pickService = (s: ApptService) => {
    setService(s);
    setDate(''); setSlot(null);
    setStep('date');
    setDatesLoading(true);
    publicBookingService.availableDates(slug!, s.serviceUid, 14)
      .then((r) => setDates(r.dates ?? []))
      .catch(() => setDates([]))
      .finally(() => setDatesLoading(false));
  };

  const pickDate = (d: string) => {
    setDate(d);
    setSlot(null);
    setStep('slot');
    setSlotsLoading(true);
    publicBookingService.slots(slug!, service!.serviceUid, d)
      .then((r) => setSlots(r.slots ?? []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  };

  const submit = async () => {
    const phoneDigits = phone.replace(/\D/g, '');
    if (!name.trim()) { toast.error('Your name is required'); return; }
    if (!phoneDigits) { toast.error('Your phone number is required'); return; }
    setBooking(true);
    try {
      const r = await publicBookingService.book(slug!, {
        serviceUid: service!.serviceUid, bookingDate: date,
        slotStart: slot!.start, slotEnd: slot!.end,
        customerName: name.trim(), customerPhone: phoneDigits,
        customerEmail: email.trim() || undefined, notes: notes.trim() || undefined,
      });
      setConfirmed({ bookingNo: r.bookingNo });
      setStep('done');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Couldn't complete the booking — try another slot.");
    }
    setBooking(false);
  };

  const loadMine = useCallback(async () => {
    const digits = minePhone.replace(/\D/g, '');
    if (digits.length < 6) { toast.error('Enter your phone number'); return; }
    setMineLoading(true);
    try {
      const r = await publicBookingService.myBookings(slug!, digits);
      setMine(r.items ?? []);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Couldn't find your bookings.");
    }
    setMineLoading(false);
  }, [minePhone, slug]);

  const doCancel = async () => {
    if (!cancelFor) return;
    setCancelBusy(true);
    try {
      await publicBookingService.cancel(slug!, {
        phone: minePhone.replace(/\D/g, ''), bookingUid: cancelFor.bookingUid, reason: 'Cancelled by customer',
      });
      toast.success('Booking cancelled');
      setCancelFor(null);
      void loadMine();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Couldn't cancel this booking.");
    }
    setCancelBusy(false);
  };

  const restart = () => {
    setStep('service'); setService(null); setDate(''); setSlot(null);
    setName(''); setPhone(''); setEmail(''); setNotes(''); setConfirmed(null);
  };

  const upcoming = useMemo(() => (mine ?? []).filter((b) => b.status === 'PENDING' || b.status === 'CONFIRMED'), [mine]);
  const past = useMemo(() => (mine ?? []).filter((b) => b.status !== 'PENDING' && b.status !== 'CONFIRMED'), [mine]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center gap-2 text-text-muted">
        <Loader2 size={18} className="animate-spin" /> Loading…
      </div>
    );
  }

  if (notFound || !company) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-surface border border-border rounded-2xl shadow-soft p-8 text-center">
          <h1 className="font-display text-xl font-semibold text-text-primary mb-2">Booking link not found</h1>
          <p className="text-sm text-text-muted">This booking link doesn't exist or is no longer available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg py-8 px-4">
      <div className="mx-auto max-w-2xl">
        {/* header */}
        <div className="rounded-2xl overflow-hidden border border-border bg-surface shadow-soft mb-4">
          <div className="bg-gold-gradient px-6 py-6 text-white flex items-center gap-4">
            {company.logoUrl ? (
              <img src={company.logoUrl} alt="" className="w-14 h-14 rounded-2xl bg-white/95 object-contain ring-4 ring-white/25 shrink-0" />
            ) : (
              <span className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center shrink-0"><CalendarClock size={26} /></span>
            )}
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/80">Book an appointment</div>
              <h1 className="font-display text-[22px] font-bold leading-tight truncate">{company.name}</h1>
            </div>
          </div>
          {/* tabs */}
          <div className="flex border-t border-border">
            {([['book', 'Book'], ['mine', 'My bookings']] as [Tab, string][]).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setTab(k)}
                className={clsx(
                  'flex-1 py-2.5 text-[13px] font-semibold transition-colors border-none cursor-pointer',
                  tab === k ? 'bg-primary-soft text-primary' : 'bg-transparent text-text-muted hover:text-text-primary',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'book' ? (
          <div className="rounded-2xl border border-border bg-surface shadow-soft p-5">
            {/* step: pick a service */}
            {step === 'service' && (
              <>
                <h2 className="text-[15px] font-bold text-text-primary mb-3">Pick a service</h2>
                {services.length === 0 ? (
                  <div className="py-10 text-center text-[13px] text-text-muted">No services are currently accepting bookings.</div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {services.map((s) => (
                      <button
                        key={s.serviceUid}
                        type="button"
                        onClick={() => pickService(s)}
                        className="flex items-center gap-3 w-full rounded-xl border border-border hover:border-primary/50 hover:bg-primary-soft/30 bg-surface px-3 py-3 text-left transition-colors cursor-pointer"
                      >
                        {s.imageUrl ? (
                          <img src={s.imageUrl} alt="" className="w-12 h-12 rounded-xl object-cover object-top shrink-0" />
                        ) : (
                          <span className="w-12 h-12 rounded-xl bg-primary-soft text-primary flex items-center justify-center shrink-0"><CalendarClock size={20} /></span>
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block text-[14px] font-semibold text-text-primary truncate">{s.name}</span>
                          <span className="block text-[12px] text-text-muted line-clamp-1">{s.description || ''}</span>
                          <span className="flex items-center gap-1.5 mt-1">
                            <span className="inline-flex items-center gap-1 text-[10.5px] font-semibold bg-surface-alt rounded-full px-2 py-[2px] text-text-primary"><Clock size={9} /> {s.durationMinutes} min</span>
                            {s.price != null && (
                              <span className="inline-flex items-center text-[10.5px] font-semibold bg-primary-soft text-primary rounded-full px-2 py-[2px]">{s.currency || 'AED'} {s.price}</span>
                            )}
                          </span>
                        </span>
                        <Users size={15} className="text-text-muted shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* step: pick a date */}
            {step === 'date' && service && (
              <>
                <BackRow onBack={() => setStep('service')} label={service.name} />
                <h2 className="text-[15px] font-bold text-text-primary mb-3">Pick a date</h2>
                {datesLoading ? (
                  <div className="flex items-center gap-2 text-[13px] text-text-muted py-6"><Loader2 size={15} className="animate-spin" /> Finding available dates…</div>
                ) : dates.length === 0 ? (
                  <div className="py-8 text-center text-[13px] text-text-muted">No availability in the coming days — please check back later.</div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {dates.map((d) => (
                      <button
                        key={d.date}
                        type="button"
                        onClick={() => pickDate(d.date)}
                        className="rounded-xl border border-border hover:border-primary/60 hover:bg-primary-soft/30 bg-surface px-3 py-2.5 text-left transition-colors cursor-pointer"
                      >
                        <span className="block text-[11px] font-semibold uppercase tracking-wide text-text-muted">{d.weekday}</span>
                        <span className="block text-[14px] font-bold text-text-primary">
                          {new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                        </span>
                        <span className="block text-[11px] text-primary font-medium mt-0.5">{d.slotCount} slot{d.slotCount === 1 ? '' : 's'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* step: pick a slot */}
            {step === 'slot' && service && (
              <>
                <BackRow onBack={() => setStep('date')} label={`${service.name} · ${niceDate(date)}`} />
                <h2 className="text-[15px] font-bold text-text-primary mb-3">Pick a time</h2>
                {slotsLoading ? (
                  <div className="flex items-center gap-2 text-[13px] text-text-muted py-6"><Loader2 size={15} className="animate-spin" /> Loading times…</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                    {slots.map((s) => (
                      <button
                        key={s.start}
                        type="button"
                        disabled={!s.available}
                        onClick={() => { setSlot(s); setStep('details'); }}
                        className={clsx(
                          'border rounded-lg px-1 py-2 text-[12.5px] font-medium transition-colors cursor-pointer',
                          s.available
                            ? 'bg-surface border-border text-text-primary hover:border-primary/60 hover:bg-primary-soft/40'
                            : 'bg-surface-alt/60 border-border text-text-muted/50 line-through cursor-not-allowed',
                        )}
                      >
                        {to12h(s.start)}
                      </button>
                    ))}
                    {slots.length === 0 && <div className="col-span-full py-6 text-center text-[13px] text-text-muted">No times on this day.</div>}
                  </div>
                )}
              </>
            )}

            {/* step: your details */}
            {step === 'details' && service && slot && (
              <>
                <BackRow onBack={() => setStep('slot')} label={`${service.name} · ${niceDate(date)} · ${to12h(slot.start)}`} />
                <h2 className="text-[15px] font-bold text-text-primary mb-3">Your details</h2>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1.5">Full name</label>
                    <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Ahmed Ali" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1.5">Phone</label>
                    <PhoneField value={phone} onChange={setPhone} />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1.5">Email (optional)</label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1.5">Notes (optional)</label>
                    <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything we should know…" />
                  </div>
                  <Button onClick={submit} loading={booking} className="mt-1">
                    Confirm booking — {to12h(slot.start)} on {new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                  </Button>
                </div>
              </>
            )}

            {/* step: confirmed */}
            {step === 'done' && confirmed && service && slot && (
              <div className="py-6 text-center">
                <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-success-soft flex items-center justify-center">
                  <Check size={26} strokeWidth={3} className="text-success" />
                </div>
                <h2 className="font-display text-[19px] font-bold text-text-primary">Booking confirmed!</h2>
                <p className="text-[13px] text-text-muted mt-1">
                  {service.name} · {niceDate(date)} · {to12h(slot.start)} – {to12h(slot.end)}
                </p>
                <div className="inline-flex items-center gap-1.5 mt-3 text-[13px] font-semibold text-primary bg-primary-soft rounded-full px-3.5 py-1.5">
                  <Hash size={12} /> {confirmed.bookingNo}
                </div>
                <p className="text-[12px] text-text-muted mt-3">Keep this reference — you can manage the booking in "My bookings" with your phone number.</p>
                <Button variant="secondary" className="mt-5" onClick={restart}>Book another appointment</Button>
              </div>
            )}
          </div>
        ) : (
          /* ── my bookings ── */
          <div className="rounded-2xl border border-border bg-surface shadow-soft p-5">
            <h2 className="text-[15px] font-bold text-text-primary mb-3">Find my bookings</h2>
            <div className="flex gap-2 items-start">
              <PhoneField value={minePhone} onChange={setMinePhone} className="flex-1" />
              <Button onClick={() => void loadMine()} loading={mineLoading} iconLeft={<Search size={15} />}>Find</Button>
            </div>

            {mine !== null && (
              <div className="mt-4">
                {mine.length === 0 ? (
                  <div className="py-8 text-center text-[13px] text-text-muted">No bookings found for this number.</div>
                ) : (
                  <div className="flex flex-col gap-4">
                    {upcoming.length > 0 && (
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wide text-text-muted mb-2">Upcoming</div>
                        <div className="flex flex-col gap-2">
                          {upcoming.map((b) => (
                            <MyBookingRow key={b.bookingUid} b={b} onCancel={() => setCancelFor(b)} onReschedule={() => setReschedFor(b)} />
                          ))}
                        </div>
                      </div>
                    )}
                    {past.length > 0 && (
                      <div>
                        <div className="text-[11px] font-bold uppercase tracking-wide text-text-muted mb-2">Past & cancelled</div>
                        <div className="flex flex-col gap-2">
                          {past.map((b) => <MyBookingRow key={b.bookingUid} b={b} />)}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <p className="text-center text-[11px] text-text-muted mt-6">Powered by SlightorCRM</p>
      </div>

      {/* cancel confirmation */}
      {cancelFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4" onClick={() => setCancelFor(null)}>
          <div className="w-full max-w-sm rounded-2xl border border-border bg-surface shadow-elevated p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-base font-semibold text-text-primary mb-1">Cancel {cancelFor.bookingNo}?</h3>
            <p className="text-[13px] text-text-muted mb-4">
              {cancelFor.serviceName} · {niceDate(cancelFor.bookingDate)} · {to12h(cancelFor.slotStart)}
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => setCancelFor(null)}>Keep it</Button>
              <Button variant="danger" size="sm" onClick={() => void doCancel()} loading={cancelBusy}>Cancel booking</Button>
            </div>
          </div>
        </div>
      )}

      {/* reschedule */}
      {reschedFor && (
        <RescheduleDialog
          slugValue={slug!}
          booking={reschedFor}
          phone={minePhone.replace(/\D/g, '')}
          onClose={() => setReschedFor(null)}
          onDone={() => { setReschedFor(null); void loadMine(); }}
        />
      )}
    </div>
  );
}

const BackRow = ({ onBack, label }: { onBack: () => void; label: string }) => (
  <button
    type="button"
    onClick={onBack}
    className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-text-muted hover:text-text-primary transition-colors mb-3 border-none bg-transparent cursor-pointer"
  >
    <ChevronLeft size={14} /> {label}
  </button>
);

const MyBookingRow = ({ b, onCancel, onReschedule }: { b: ApptBooking; onCancel?: () => void; onReschedule?: () => void }) => {
  const meta = APPT_STATUS_META[b.status];
  const cancelled = b.status === 'CANCELLED';
  return (
    <div className={clsx('rounded-xl border border-border bg-surface px-3.5 py-3', cancelled && 'opacity-70')}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12px] font-semibold text-primary">{b.bookingNo}</span>
        <span className={clsx('text-[10.5px] font-semibold rounded-full px-2 py-0.5', meta?.className)}>{meta?.label ?? b.status}</span>
        <span className="ml-auto text-[12px] text-text-muted whitespace-nowrap">
          {new Date(`${b.bookingDate}T00:00:00`).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })} · {to12h(b.slotStart)}
        </span>
      </div>
      <div className={clsx('text-[13.5px] font-semibold text-text-primary mt-1', cancelled && 'line-through')}>{b.serviceName}</div>
      {b.notes && (
        <div className="text-[12px] text-text-muted mt-0.5 flex items-start gap-1.5"><StickyNote size={11} className="mt-[2px] shrink-0" /> {b.notes}</div>
      )}
      {cancelled && b.cancelReason && (
        <div className="text-[11.5px] text-danger mt-1 flex items-start gap-1.5"><XCircle size={11} className="mt-[2px] shrink-0" /> {b.cancelReason}</div>
      )}
      {(onCancel || onReschedule) && (
        <div className="flex gap-2 mt-2.5">
          {onReschedule && (
            <button type="button" onClick={onReschedule}
              className="h-7 px-2.5 rounded-lg border border-border bg-surface hover:bg-surface-alt text-[12px] font-medium text-text-primary cursor-pointer inline-flex items-center gap-1.5 transition-colors">
              <CalendarDays size={12} /> Reschedule
            </button>
          )}
          {onCancel && (
            <button type="button" onClick={onCancel}
              className="h-7 px-2.5 rounded-lg border border-danger/30 bg-surface hover:bg-danger-soft text-[12px] font-medium text-danger cursor-pointer inline-flex items-center gap-1.5 transition-colors">
              <X size={12} /> Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const RescheduleDialog = ({ slugValue, booking, phone, onClose, onDone }: {
  slugValue: string;
  booking: ApptBooking;
  phone: string;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [dates, setDates] = useState<Array<{ date: string; weekday: string; slotCount: number }>>([]);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState<ApptSlot[]>([]);
  const [slot, setSlot] = useState<ApptSlot | null>(null);
  const [loadingDates, setLoadingDates] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!booking.serviceUid) { setDates([]); setLoadingDates(false); return; }
    publicBookingService.availableDates(slugValue, booking.serviceUid, 14)
      .then((r) => setDates(r.dates ?? []))
      .catch(() => setDates([]))
      .finally(() => setLoadingDates(false));
  }, [slugValue, booking.serviceUid]);

  const pickDate = (d: string) => {
    setDate(d); setSlot(null); setLoadingSlots(true);
    publicBookingService.slots(slugValue, booking.serviceUid!, d)
      .then((r) => setSlots(r.slots ?? []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  };

  const confirm = async () => {
    if (!date || !slot) return;
    setBusy(true);
    try {
      await publicBookingService.reschedule(slugValue, {
        phone, bookingUid: booking.bookingUid,
        bookingDate: date, slotStart: slot.start, slotEnd: slot.end,
      });
      toast.success('Booking rescheduled');
      onDone();
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Couldn't reschedule — try another time.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm px-4 overflow-y-auto py-8" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface shadow-elevated p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-display text-base font-semibold text-text-primary mb-1">Reschedule {booking.bookingNo}</h3>
        <p className="text-[13px] text-text-muted mb-3">
          Currently {niceDate(booking.bookingDate)} · {to12h(booking.slotStart)}
        </p>

        <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mb-1.5">New date</div>
        {loadingDates ? (
          <div className="flex items-center gap-2 text-[13px] text-text-muted py-3"><Loader2 size={15} className="animate-spin" /> Loading…</div>
        ) : (
          <div className="flex gap-1.5 overflow-x-auto pb-1.5">
            {dates.map((d) => (
              <button
                key={d.date}
                type="button"
                onClick={() => pickDate(d.date)}
                className={clsx(
                  'shrink-0 rounded-lg border px-2.5 py-1.5 text-left transition-colors cursor-pointer',
                  date === d.date ? 'border-primary bg-primary-soft text-primary' : 'border-border bg-surface text-text-primary hover:border-primary/50',
                )}
              >
                <span className="block text-[10px] font-semibold uppercase">{d.weekday.slice(0, 3)}</span>
                <span className="block text-[12.5px] font-bold">{new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</span>
              </button>
            ))}
            {dates.length === 0 && <span className="text-[12.5px] text-text-muted py-2">No upcoming availability.</span>}
          </div>
        )}

        {date && (
          <>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted mt-3 mb-1.5">New time</div>
            {loadingSlots ? (
              <div className="flex items-center gap-2 text-[13px] text-text-muted py-3"><Loader2 size={15} className="animate-spin" /> Loading times…</div>
            ) : (
              <div className="grid grid-cols-4 gap-1.5 max-h-[176px] overflow-y-auto pr-1">
                {slots.map((s) => {
                  const sel = slot?.start === s.start;
                  return (
                    <button
                      key={s.start}
                      type="button"
                      disabled={!s.available}
                      onClick={() => setSlot(s)}
                      className={clsx(
                        'border rounded-lg px-1 py-[7px] text-[12px] font-medium transition-colors cursor-pointer',
                        sel ? 'bg-primary text-on-primary border-primary shadow-soft'
                          : s.available ? 'bg-surface border-border text-text-primary hover:border-primary/60 hover:bg-primary-soft/40'
                          : 'bg-surface-alt/60 border-border text-text-muted/50 line-through cursor-not-allowed',
                      )}
                    >{to12h(s.start)}</button>
                  );
                })}
                {slots.length === 0 && <div className="col-span-full py-4 text-center text-[12.5px] text-text-muted">No times on this day.</div>}
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="secondary" size="sm" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button size="sm" onClick={() => void confirm()} disabled={busy || !slot} loading={busy}>
            {slot ? `Move to ${to12h(slot.start)}` : 'Pick a time'}
          </Button>
        </div>
      </div>
    </div>
  );
};
